import { useState, useEffect, useContext } from "react";
import ReactGA from "react-ga";
import { useSearchParams } from "react-router-dom";
import { ApiContext } from "../../Contexts/ApiContext";
import { Loader } from "../Loader/Loader";
import { WaveLoader } from "../Loader/WaveLoader";
import { CheckoutAgreement } from "./CheckoutAgreement";
import { mapCurrencyIntoHtmlCodeAndSymbol } from "../../Utils/currencyUtils";
import { convertFromNumberToPeriod } from "../../Utils/creditCardUtils";
import { constants } from "../../Utils/constants";
import "../../Components/Offer/Offer.css";
import zeroImage from "./img/zeroImage.svg";
import vGreen from "./img/vGreen.svg";
import vAlternate from "./img/vAlternate.svg";
import gift from "./img/gift.png";

  export const Offer = ({ isCollapsed }) => {
  const { fetchOffer, postEvent, currencies } = useContext(ApiContext);
  const [queryParams] = useSearchParams();
  const token = queryParams.get("id");
  const [offerList, setOfferList] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState();
  const [selectedClassName, setSelectedClassName] = useState("default");
  const [alternateText, setAlternateText] = useState('');
  const [plannedCycle, setPlannedCycle] = useState();
  const [cpt, setCpt] = useState();
  const [amountForDescription, setAmountForDescription] = useState();
  const [offerTermsList, setOfferTermsList] = useState();
  const [numberOnArrow, setNumberOnArrow] = useState();
  const [currencySymbol, setCurrencySymbol] = useState();
  const [mitigationObj, setMitigationObj] = useState();
  const [headline, setHeadline] = useState();
  const [taxIsRequired, setTaxIsRequired] = useState(true);
  const [totalAmount, setTotalAmount] = useState();
  const [currentOffer, setCurrentOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const RECALC_POLL_INTERVAL = 3000;
  const RECALC_MAX_POLL_COUNT = 20;

  useEffect(() => {
    if (token) {
      getOfferDetails(token);
      handlePostEvent({eventName: "page load", origin: 'ecom', url: window.location.href, details: `offer page has loaded successfully`});
    }
  }, [token, currencies]);

  useEffect(() => {
    if (!token || !currentOffer?.recalc_pending) return;
    // offer_id is a freshly-signed JWT on every response (non-deterministic iat), so it
    // can't be used to identify the same offer across polls - match on product_id instead.
    const selectedProductId = currentOffer.product_id;
    let pollCount = 0;
    const pollFunction = async () => {
      pollCount += 1;
      if (pollCount >= RECALC_MAX_POLL_COUNT) {
        clearInterval(polling);
        return;
      }
      try {
        const resp = await fetchOffer(token);
        setOfferList(resp.data.offers);
        const refreshedOffer = resp.data.offers.find((offer) => offer.product_id === selectedProductId);
        if (refreshedOffer && !refreshedOffer.recalc_pending) {
          setDetailsBySelectedOfferId(refreshedOffer);
          clearInterval(polling);
        }
      } catch (error) {
        console.error(error);
      }
    };
    const polling = setInterval(pollFunction, RECALC_POLL_INTERVAL);
    return () => clearInterval(polling);
  }, [token, currentOffer?.recalc_pending, currentOffer?.product_id]);

  useEffect(() => {
    if (!selectedOfferId) return;
    const offer = offerList.find((offer) => offer.offer_id === selectedOfferId);
    setDetailsBySelectedOfferId(offer);
    sessionStorage.setItem("defaultProduct", offer.product_id);
  }, [selectedOfferId]);

  const getOfferDetails = async (token) => {
    setIsLoading(true);
    try {
      const resp = await fetchOffer(token);
      window.parent.postMessage(
        {
          message: "iot_dom_loaded",
        },
        "*"
      );
      const { currencySymbol } = mapCurrencyIntoHtmlCodeAndSymbol(
        resp.data.offers[0].currency,
        currencies
      );
      setCurrencySymbol(currencySymbol);
      setOfferList(resp.data.offers);
      setTaxIsRequired(resp.data.taxIsRequired);
      const sessionDefaultProduct = sessionStorage.getItem('defaultProduct');
      const DEFAULT_PRODUCT_ID = sessionDefaultProduct ? setDefaultBySession(parseInt(sessionDefaultProduct), resp.data) : resp.data.defaultProductId;
      const defaultOffer = resp.data.offers.find(
        (offer) => offer.product_id === DEFAULT_PRODUCT_ID
      );
      handleSaveSelectedOffer(defaultOffer.offer_id);
      const mappedText = mapSwitchText(defaultOffer.product_id);
      setAlternateText(mappedText);
      handleHeadline(defaultOffer.product_id);
    } finally {
      setIsLoading(false);
    }
  };

  const setDefaultBySession = (sessionDefaultProduct, response) => {
    let productId = response.defaultProductId;
    const sessionProductOffer = response.offers.find((offer) => offer.product_id === sessionDefaultProduct);
    if (sessionProductOffer) {
      productId = sessionProductOffer.product_id;
    }
    return productId;
  }

  const setDetailsBySelectedOfferId = (offer) => {
    setCurrentOffer(offer);
    setCpt(offer.business_terms.cost_per_tx);
    determineCycleByProductId(
      offer.product_id,
      offer.billing_cycle,
      offer.business_terms.planned_cycle
    );
    determineAmountForDescriptionByProductId(
      offer.product_id,
      offer.business_terms.share,
      offer.business_terms.cost_per_tx,
      offer.business_terms.planned_amount,
      offer.currency
    );
    generateOfferTermsList(
      offer.down_payment,
      offer.business_terms.min,
      offer.relevantPolicies,
      offer.currency,
      offer.business_terms.planned_cycle,
      offer.product_id
    );
    setMitigationObj({
      popup_url: offer.popup_url,
      needs_mitigation: offer.needs_mitigation,
    });
    setTotalAmount(offer.total_amount);
    
    // Set headline based on product type
    if (offer.product_id === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN) {
      setHeadline('Your payment plan');
    } else {
      setHeadline('Your revenue payment plan');
    }
  };

  const generateOfferTermsList = (
    downPayment,
    minInstallment,
    policies,
    currency,
    plannedCycle,
    productId
  ) => {
    let termList = [];
    const { currencySymbol } = mapCurrencyIntoHtmlCodeAndSymbol(currency, currencies);
    // Don't show down payment for Product 9 (FIXED_PAYMENT_PLAN)
    if (downPayment && productId !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN) {
      termList.push(`${currencySymbol}${downPayment} down payment`);
    }
    if (minInstallment) {
      termList.push(
        `${currencySymbol}${minInstallment} ${convertFromNumberToPeriod(
          plannedCycle
        )} minimum payment`
      );
    }
    if (policies.length > 0) {
      policies.forEach((policy) => {
        if (policy === "Card Guarantee") {
          policy = "card guarantee is required";
        }
        termList.push(policy);
      });
    }
    setOfferTermsList(termList);
  };

  const determineCycleByProductId = (productId, billingCycle, plannedCycle) => {
    if (productId === constants.PRODUCT_IDS.MCA) {
      setPlannedCycle(convertFromNumberToPeriod(billingCycle));
    } else {
      setPlannedCycle(convertFromNumberToPeriod(plannedCycle));
    }
  };

  const determineAmountForDescriptionByProductId = (
    productId,
    share,
    cpt,
    fixedAmount,
    currency
  ) => {
    const { currencySymbol } = mapCurrencyIntoHtmlCodeAndSymbol(currency, currencies);
    const fixedPaymentPlanIds = [
      constants.PRODUCT_IDS.FIXED_PAYMENT,
      constants.PRODUCT_IDS.CALCULATED_FIXED_PAYMENT,
    ];
    
    if (productId === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN) {
      // For payment plan, we don't need the standard description format
      const number = `${currencySymbol}${fixedAmount}`;
      setAmountForDescription(number);
      setNumberOnArrow(number);
    } else if (fixedPaymentPlanIds.includes(productId)) {
      const number = `${currencySymbol}${fixedAmount}`;
      setAmountForDescription(number);
      setNumberOnArrow(number);
    } else if (productId === constants.PRODUCT_IDS.MCA_PLUS_CPT_WITH_MIN) {
      const amount = `${share}% + ${currencySymbol}${cpt} per transaction`;
      const number = `${share}%`;
      setAmountForDescription(amount);
      setNumberOnArrow(number);
    } else {
      const number = `${share}%`;
      setAmountForDescription(number);
      setNumberOnArrow(number);
    }
  };

  const postMsgToHost = () => {
    if (currentOffer?.recalc_pending) return;
    ReactGA.event({
      category: "NayaxCapital",
      action: "CheckOut_Click",
    });
    if (mitigationObj.needs_mitigation === true) {
      window.parent.postMessage(
        {
          message: "iot_open_popup",
          popup_url: mitigationObj.popup_url,
        },
        "*"
      );
    } else if (mitigationObj.needs_mitigation === false) {
      window.parent.postMessage(
        {
          message: "iot_offer_select",
          unique_token: selectedOfferId,
        },
        "*"
      );
    }
  };

  const handleHeadline = (productId) => {
    let title = "Pay As You Grow";
    switch (productId) {
      case constants.PRODUCT_IDS.MCA_WITH_MIN:
        title = "Pay As You Grow";
        break;
      case constants.PRODUCT_IDS.FIXED_PAYMENT:
        title = "Fixed Payment Plan";
        break;
      case constants.PRODUCT_IDS.CALCULATED_FIXED_PAYMENT:
        title = "Fixed Payment Plan";
        break;
      case constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN:
        title = "Fixed Payment Plan";
        break;
      default:
        title = "Pay As You Grow";
        break;
    }
    window.parent.postMessage(
      {
        message: "iot_title",
        title: title,
      },
      "*"
    );
  }

  const handleSwitchOffer = () => {
    const alternateOffer = offerList.find(
      (offer) => offer.offer_id !== selectedOfferId
    );
    handleSaveSelectedOffer(alternateOffer.offer_id);
    setSelectedClassName(
      selectedClassName === "alternate" ? "default" : "alternate"
    );
    handleHeadline(alternateOffer.product_id);
    const mappedText = mapSwitchText(alternateOffer.product_id);
    setAlternateText(mappedText);
    handlePostEvent({eventName: "switch offer", origin: 'ecom', url: window.location.href, details: `switched to product id ${alternateOffer.product_id}`});
  };

  const handlePostEvent = (eventObj) => {
    postEvent(eventObj);
  }

  const handleSaveSelectedOffer = (offerId) => {
    setSelectedOfferId(offerId);
  }

  const mapSwitchText = (productId) => {
    let mappedText = 'I want another plan';
    switch (productId) {
      case constants.PRODUCT_IDS.MCA_WITH_MIN:
        mappedText = 'I want a "fixed payment" plan';
        break;
      case constants.PRODUCT_IDS.FIXED_PAYMENT:
        mappedText = 'I want a "pay as you grow" plan';
        break;
      case constants.PRODUCT_IDS.CALCULATED_FIXED_PAYMENT:
        mappedText = 'I want a "pay as you grow" plan';
        break;
      default:
        mappedText = 'I want another plan';
        break;
    }
    return mappedText;
  };

  if (isLoading) {
    return (
      <div className="loader-container yellow-bg">
        <Loader />
      </div>
    );
  }

  return (
    <div
      className={
        isCollapsed === false && offerList.length > 0
          ? `yellow-bg container hurme-3-regular letter-spacing ${currentOffer?.product_id === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN ? 'product-9-container' : ''}`
          : `white-bg container hurme-3-regular letter-spacing is-collapsed ${currentOffer?.product_id === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN ? 'product-9-container' : ''}`
      }
    >
      <div className='full-container'>
        {!taxIsRequired && (
          <div className={`tax-exemption-container ${isCollapsed ? 'is-collapsed' : ''}`}>
            <div className='tax-exemption-left'>
              <div className='tax-exemption-image'>
                <img src={gift} alt='gift'></img>
              </div>
              <div className='tax-exemption-text'>
                <p>Congrats! Nayax Capital is currently exempt</p>
                <p>from sales tax in your region.</p>
              </div>
            </div>
            <div className='tax-exemption-amount'>
              <div>
                <p className='tax-exemption-amount-text'>Pay Only</p>
              </div>
              <div className='tax-exemption-amount-number'>
                {currencySymbol} {totalAmount?.toLocaleString()}
              </div>
            </div>
          </div>
        )}
        {isCollapsed === false && currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
          <div className='headline-container'>
            <p>{headline}</p>
          </div>
        )}
        
        {/* Standard Offer Display for other products */}
        {currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
          <div className={`description-container ${isCollapsed ? 'is-collapsed' : ''}`}>
            <div className='arrow-container'>
              <div className={`arrow ${selectedClassName}`}>
                <div className='description-numbers-container'>
                  <div className='description-numbers-left'>
                    <span>{numberOnArrow}</span>
                  </div>
                  {cpt && (
                    <div className='description-numbers-right'>
                      <div className='description-numbers-right-top'>
                        <span className='plus-sign'>+</span>
                        <span>
                          {currencySymbol}
                          {cpt}
                        </span>
                      </div>
                      <div className='description-numbers-right-bottom'>
                        <p>Per transaction</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='description-text'>
              <p>
                A {plannedCycle} deduction of {amountForDescription} will be taken
                from your total cashless sales until the order is paid in full
              </p>
            </div>
          </div>
        )}

        {/* Always show P9 payment plan, but conditionally show other content */}
        {currentOffer?.product_id === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
          <div className='payment-plan-container'>
            <div className='payment-plan-amount'>
            {currentOffer?.recalc_pending ? (
              <div className='offer-recalc-pending'>
                <h3 className='offer-recalc-pending-title'>Preparing your personalized offer</h3>
                <p className='offer-recalc-pending-subtitle'>This usually takes just a few seconds</p>
                <WaveLoader />
              </div>
            ) : isCollapsed ? (
              <div className='p9-collapsed-card'>
                <span className='p9-collapsed-pill'>0% Interest</span>
                <p className='p9-collapsed-subtitle'>
                  Pay in {currentOffer?.business_terms?.default_number_of_payments} equal monthly installments
                </p>
                <div className='p9-collapsed-amount-row'>
                  <span className='p9-collapsed-amount'>
                    ${currentOffer?.business_terms?.planned_amount}
                    <span className='p9-collapsed-amount-x'>×</span>
                    {currentOffer?.business_terms?.default_number_of_payments}
                  </span>
                  <span className='p9-card-icon'></span>
                </div>
                <div className='p9-collapsed-footer'>
                  <span className='p9-collapsed-check'>✓</span>
                  <span>No fees. No hidden costs.</span>
                </div>
              </div>
            ) : (
              <>
                <div className='payment-plan-header'>
                  <h3>Pay in {currentOffer?.business_terms?.default_number_of_payments} equal monthly installments</h3>
                </div>
                <div className='monthly-payment'>
                  <span className={`amount-large${isCollapsed ? ' collapsed' : ''}`}>
                    ${currentOffer?.business_terms?.planned_amount}
                  </span>
                  <span className={`multiplication${isCollapsed ? ' collapsed' : ''}`}>x{currentOffer?.business_terms?.default_number_of_payments}</span>
                  <span className='p9-card-icon'></span>
                </div>
                <div className='advance-payment'>
                  <span className='advance-text'>First payment, tax and shipping, are due in advance</span>
                </div>
                {/* {currentOffer?.business_terms?.pay_now === false && (
                  <p className='rounding-disclaimer hurme-3-regular'>
                    Tax and shipping are charged separately.
                  </p>
                )} */}
              </>
            )}
            </div>
            {/* Show these elements only when expanded for P9 */}
            {isCollapsed === false && (
              <>
                {/* Zero interest section right after advance payment for P9 */}
                <div className={`zero-container zero-container-small`}>
                  {currentOffer?.product_id === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN ? (
                    <div className='zero-big-text'>
                      <p>0%</p>
                      </div>
                  ) : (
                  <div className='zero-img-container'>
                    <img src={zeroImage} alt='zero'></img>
                  </div>
                  )}
                  <div className='zero-text'>
                    <p>Zero interest rate</p>
                    <p className='zero-text-last-line'>
                      No hidden costs, no onboarding fees, no penalties
                    </p>
                  </div>
                </div>
                {/* Checkout button for P9 expanded view */}
                <div className={`bottom-offer-page ${taxIsRequired ? 'tax-exempt-false' : 'tax-exempt-true'}`}>
                  <div className='checkout-button-container'>
                    <button className='checkout-button' onClick={postMsgToHost}>
                      {currentOffer?.recalc_pending && <span className='checkout-button-spinner'></span>}
                      Check Out with Nayax Capital
                    </button>
                  </div>
                  <CheckoutAgreement operatingEntityId={currentOffer?.business_terms?.operating_entity_id} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Standard collapsible content for non-P9 products */}
        {isCollapsed === false && offerList.length > 0 && currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
          <div className='div-collapsible'>
          { offerList.length > 1 && currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN &&
            <div className='alternate-div'>
              <span className='alternate-text'>{alternateText}</span>
              <button
                className={`alternate-button ${selectedClassName}`}
                onClick={handleSwitchOffer}
              >
                <img
                  src={constants.IMAGE_URLS.RIGHT_ARROW}
                  alt='right-arrow'
                  className='arrow-icon'
                ></img>
              </button>
            </div>
          }
            {/* Zero interest for non-P9 products */}
            {currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
              <div className='zero-container'>
                <div className='zero-img-container'>
                  <img src={zeroImage} alt='zero'></img>
                </div>
                <div className='zero-text'>
                  <p>Zero interest rate</p>
                  <p className='zero-text-last-line'>
                    No hidden costs, no onboarding fees, no penalties
                  </p>
                </div>
              </div>
            )}
              {/* Business terms and checkout - only for non-P9 products */}
              {currentOffer?.product_id !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
                <>
                  <div className='offer-business-terms-container'>
                    {offerTermsList &&
                      offerTermsList.map((term, i) => {
                        return (
                          <div className='offer-term' key={i}>
                            <img
                              src={
                                selectedClassName === "alternate"
                                  ? vAlternate
                                  : vGreen
                              }
                              alt='v-icon'
                            ></img>
                            <p className='offer-term-text'>{term}</p>
                          </div>
                        );
                      })}
                  </div>
                  <div className={`bottom-offer-page ${taxIsRequired ? 'tax-exempt-false' : 'tax-exempt-true'}`}>
                    <div className='checkout-button-container'>
                      <button className='checkout-button' onClick={postMsgToHost}>
                        Check Out with Nayax Capital
                      </button>
                    </div>
                    <CheckoutAgreement operatingEntityId={currentOffer?.business_terms?.operating_entity_id} />
                  </div>
                </>
              )}
            </div>
        )}
      </div>
    </div>
  );
};
